import React, { useState } from 'react';
import { Question } from '../types';
import { BookOpen, ChevronDown, ChevronUp, CheckCircle, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

interface FormulaQuestionCardProps {
  question: Question;
}

export const FormulaQuestionCard: React.FC<FormulaQuestionCardProps> = ({ question }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  return (
    <div className="rounded-lg border border-paper-300 bg-paper-50 p-3.5 shadow-sm transition hover:border-indigo-300 dark:border-study-line dark:bg-study-card dark:hover:border-indigo-600">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-paper-200 pb-2.5 dark:border-study-line">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
          <span className="font-serif text-xs font-bold text-zinc-900 dark:text-zinc-100">
            {question.source.replace(/_/g, ' ')}
            {question.questionNumber ? ` — Questão ${question.questionNumber}` : ''}
          </span>
          <span className="rounded bg-indigo-50 px-2 py-0.5 text-[10px] font-mono font-bold text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60">
            {question.subject} • Peso {question.weight}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[10px] font-mono text-zinc-400 dark:text-zinc-500">
            <Tag className="h-3 w-3" />
            Tag: {question.formulaId}
          </span>
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            {expanded ? (
              <>
                <span>Recolher</span>
                <ChevronUp className="h-3.5 w-3.5" />
              </>
            ) : (
              <>
                <span>Ver Enunciado</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Snippet / Preview */}
      {!expanded && (
        <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400 line-clamp-2 leading-relaxed">
          {question.statement.replace(/[\n\r]+/g, ' ')}
        </p>
      )}

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-3 space-y-3 pt-1">
          <div className="text-xs text-zinc-800 dark:text-zinc-200 prose prose-zinc dark:prose-invert max-w-none leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
              {question.statement}
            </ReactMarkdown>
          </div>

          {/* Alternatives */}
          {question.alternatives && question.alternatives.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-paper-200 dark:border-study-line">
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                Alternativas do Exame:
              </span>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {question.alternatives.map((alt) => {
                  const isCorrect = showAnswer && alt.id.toLowerCase() === question.correctAlternative.toLowerCase();
                  return (
                    <div
                      key={alt.id}
                      className={`flex items-start gap-2 rounded p-2 text-xs transition ${
                        isCorrect
                          ? 'border border-emerald-500 bg-emerald-50 text-emerald-950 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200 font-medium'
                          : 'border border-paper-200 bg-paper-100 text-zinc-700 dark:border-study-line dark:bg-study-panel dark:text-zinc-300'
                      }`}
                    >
                      <span className="font-mono font-bold uppercase shrink-0">
                        {alt.id})
                      </span>
                      <span>{alt.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gabarito Reveal */}
          <div className="flex items-center justify-between pt-2 border-t border-paper-200 dark:border-study-line">
            <button
              type="button"
              onClick={() => setShowAnswer((prev) => !prev)}
              className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>{showAnswer ? 'Ocultar Gabarito' : 'Revelar Gabarito Oficial'}</span>
            </button>

            {showAnswer && (
              <span className="font-mono text-xs font-bold text-emerald-700 dark:text-emerald-400">
                Gabarito: ({question.correctAlternative.toUpperCase()})
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
